/**
 * Tasks List Page
 * Displays all work-order tasks for the typing center, internal and external
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ButtonGroup,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Assignment as TaskIcon,
  Refresh as RefreshIcon,
  PersonAdd as AssignIcon,
  CheckCircle as CompleteIcon,
  FilterList as FilterIcon,
  OpenInNew as OpenWorkOrderIcon
} from '@mui/icons-material';
import TaskService from '../../services/task.service';
import TaskDetail from './TaskDetail';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { isPlatformLevel, isTcAdmin, isB2bTc } from '../../utils/roleUtils';
import { getWorkOrderStatusConfig } from '../../utils/workOrderStatus';
import { ProTableStats, useColumnPreferences } from '../../components/pro-table';
import ProTablePreferences from '../../components/pro-table/ProTablePreferences';

// Font sizes to match sidebar
const fontSize = {
  sm: '0.8125rem', // 13px
  xs: '0.75rem',   // 12px
  md: '1rem'       // 16px
};

const HEAD_CELL_SX = { fontWeight: 600, fontSize: fontSize.xs, whiteSpace: 'nowrap' };
const CELL_SX = { fontSize: '0.8125rem', py: 0.75, lineHeight: 1.4, whiteSpace: 'nowrap' };

// Long text columns are capped and truncated so column widths stay stable across pages.
const truncateSx = (maxWidth) => ({ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis' });
const truncated = (value) => (value ? <span title={value}>{value}</span> : '-');

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_KEY = 'proTable.tasks.pageSize';

const statusOf = (task) => task.status;

const buildColumns = (canSeeAllTasks, clientTerm) => [
  {
    key: 'task_number', label: 'Task #',
    sx: { fontWeight: 600, color: 'secondary.main', whiteSpace: 'nowrap' },
    render: (task) => task.task_number || '—',
  },
  { key: 'task_name', label: 'Task Name', sx: truncateSx(300), render: (task) => truncated(task.task_name) },
  {
    key: 'work_order_number', label: 'Work Order', sx: { whiteSpace: 'nowrap' },
    render: (task) => task.work_order_number || '-',
  },
  ...(canSeeAllTasks ? [
    {
      key: 'task_type', label: 'Type',
      render: (task) => (
        <Chip
          label={task.task_type === 'INTERNAL' ? 'Internal' : 'External'}
          size="small"
          variant="outlined"
          sx={{ fontSize: fontSize.xs, height: 22 }}
        />
      ),
    },
    {
      key: 'provider', label: 'Provider',
      render: (task) => {
        const cfg = task.provider_status && getWorkOrderStatusConfig(task.provider_status);
        return (
          <>
            {task.provider_name || '—'}
            {cfg && (
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={cfg.label}
                  color={cfg.color}
                  variant={cfg.variant || 'filled'}
                  size="small"
                  sx={{ fontSize: fontSize.xs, height: 20 }}
                />
              </Box>
            )}
          </>
        );
      },
    },
  ] : []),
  { key: 'organization_name', label: clientTerm, sx: truncateSx(200), render: (task) => truncated(task.organization_name) },
  { key: 'customer_name', label: 'Customer', sx: truncateSx(200), render: (task) => truncated(task.customer_name) },
  {
    key: 'assigned_to', label: 'Assigned To',
    render: (task) => (task.assigned_to_name ? (
      <Chip
        label={task.assigned_to_name}
        size="small"
        variant="outlined"
        color="primary"
        sx={{ fontSize: fontSize.xs, height: 22 }}
      />
    ) : (
      <Typography sx={{ fontSize: fontSize.xs, color: 'text.disabled' }}>
        {task.internal_task_id ? 'Unassigned' : '—'}
      </Typography>
    )),
  },
  {
    key: 'status', label: 'Status',
    render: (task) => (
      <Chip
        label={TaskService.getStatusLabel(statusOf(task))}
        color={TaskService.getStatusColor(statusOf(task))}
        size="small"
        sx={{ fontSize: fontSize.xs, height: 22 }}
      />
    ),
  },
  {
    key: 'created_at', label: 'Created',
    sx: { fontSize: fontSize.xs, color: 'text.secondary', whiteSpace: 'nowrap' },
    render: (task) => TaskService.formatDate(task.createdAt),
  },
];

const Tasks = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [taskTypeFilter, setTaskTypeFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
      return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
    } catch {
      return DEFAULT_PAGE_SIZE;
    }
  });
  const [totalTasks, setTotalTasks] = useState(0);

  const isB2b = isB2bTc(currentUser);
  const clientTerm = isB2b ? 'Typing Center' : 'Client';
  const canSeeAllTasks = isPlatformLevel(currentUser?.role) || isTcAdmin(currentUser?.role);

  const columns = useMemo(() => buildColumns(canSeeAllTasks, clientTerm), [canSeeAllTasks, clientTerm]);
  const colPrefs = useColumnPreferences('tasks', columns);

  // Task detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // A ?taskId= deep link opens that task at once; the list still loads as usual behind it.
  const deepLinkTaskId = searchParams.get('taskId');

  useEffect(() => {
    if (!deepLinkTaskId) return;
    setSelectedTaskId(deepLinkTaskId);
    setDetailDialogOpen(true);
  }, [deepLinkTaskId]);

  // Load tasks
  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (taskTypeFilter && taskTypeFilter !== 'ALL') {
        filters.taskType = taskTypeFilter;
      }
      if (searchTerm) {
        filters.search = searchTerm;
      }

      if (!canSeeAllTasks) {
        const legacyParams = {};
        if (statusFilter && statusFilter !== 'ALL') legacyParams.status = statusFilter;
        if (searchTerm) legacyParams.search = searchTerm;

        const [legacyResult, legacyStats] = await Promise.all([
          TaskService.getAll(legacyParams),
          TaskService.getStatistics()
        ]);
        const rows = (legacyResult.data || []).map(toRow);
        setTasks(rows);
        setTotalTasks(rows.length);
        setStatistics({
          total: legacyStats?.total,
          pending: legacyStats?.pending,
          in_progress: legacyStats?.inProgress,
          completed: legacyStats?.completed
        });
        return;
      }

      const listParams = { ...filters, page, size: rowsPerPage };
      if (statusFilter && statusFilter !== 'ALL') {
        listParams.status = statusFilter;
      }

      const [tasksResult, statsResult] = await Promise.all([
        TaskService.getWorkOrderTasks(listParams),
        TaskService.getWorkOrderTaskStatistics(filters)
      ]);

      setTasks(tasksResult.data || []);
      setTotalTasks(tasksResult.total || 0);
      setStatistics(statsResult);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  const pageKey = canSeeAllTasks ? `${page}:${rowsPerPage}` : 'all';

  useEffect(() => {
    loadTasks();
  }, [statusFilter, taskTypeFilter, searchTerm, pageKey]);

  const handleViewTask = (task) => {
    if (task.internal_task_id) {
      setSelectedTaskId(task.internal_task_id);
      setDetailDialogOpen(true);
    } else if (task.work_order_id) {
      navigate(`/work-orders/${task.work_order_id}`);
    }
  };

  const toRow = (t) => ({
    work_order_task_id: t.workOrderTaskId || t.id,
    internal_task_id: t.id,
    task_number: t.taskNumber,
    task_name: t.taskName,
    task_type: 'INTERNAL',
    status: t.status,
    work_order_id: t.workOrderId,
    work_order_number: t.workOrderNumber,
    organization_name: t.organizationName,
    customer_name: t.customerName,
    assigned_to: t.assignedTo,
    assigned_to_name: t.assignedToName,
    created_at: t.createdAt
  });

  const changePageSize = (size) => {
    setRowsPerPage(size);
    setPage(0);
    try { localStorage.setItem(PAGE_SIZE_KEY, String(size)); } catch { /* storage unavailable */ }
  };

  const applyFilter = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  // Handle dialog close
  const handleDetailClose = (refreshNeeded) => {
    setDetailDialogOpen(false);
    setSelectedTaskId(null);
    if (deepLinkTaskId) {
      const next = new URLSearchParams(searchParams);
      next.delete('taskId');
      setSearchParams(next, { replace: true });
    }
    if (refreshNeeded) {
      loadTasks();
    }
  };

  // Handle quick complete
  const handleQuickComplete = async (task) => {
    try {
      await TaskService.complete(task.internal_task_id);
      loadTasks();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to complete task');
    }
  };

  const visibleTasks = canSeeAllTasks
    ? tasks
    : tasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 0 }}>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={loading}
        cards={[
          { key: 'ALL', label: 'All Tasks', value: statistics.total || 0 },
          { key: 'PENDING', label: 'Pending', value: statistics.pending || 0, color: 'warning.main' },
          { key: 'IN_PROGRESS', label: 'In Progress', value: statistics.in_progress || 0, color: 'info.main' },
          { key: 'COMPLETED', label: 'Completed', value: statistics.completed || 0, color: 'success.main' },
        ].map((c) => ({
          ...c,
          highlight: statusFilter === c.key,
          onClick: () => applyFilter(setStatusFilter)(c.key),
        }))}
      />

      {/* Filters */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField
          placeholder="Search by task #, name, customer..."
          value={searchTerm}
          onChange={(e) => applyFilter(setSearchTerm)(e.target.value)}
          size="small"
          sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: fontSize.sm } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        {canSeeAllTasks && (
          <FormControl size="small" sx={{ minWidth: 140, '& .MuiInputBase-root': { height: 32 } }}>
            <InputLabel sx={{ fontSize: fontSize.sm }}>Task Type</InputLabel>
            <Select
              value={taskTypeFilter}
              label="Task Type"
              onChange={(e) => applyFilter(setTaskTypeFilter)(e.target.value)}
              sx={{ fontSize: fontSize.sm }}
            >
              <MenuItem value="ALL" sx={{ fontSize: fontSize.sm }}>All</MenuItem>
              <MenuItem value="INTERNAL" sx={{ fontSize: fontSize.sm }}>Internal</MenuItem>
              <MenuItem value="EXTERNAL" sx={{ fontSize: fontSize.sm }}>External</MenuItem>
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {totalTasks} tasks
            {statusFilter !== 'ALL' && ` (${TaskService.getStatusLabel(statusFilter)})`}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadTasks}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <ProTablePreferences
            columns={columns}
            preferences={colPrefs}
            pageSize={rowsPerPage}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={changePageSize}
          />
        </Box>
      </Box>


      {/* Tasks Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={32} />
            <Typography sx={{ ml: 2, fontSize: fontSize.sm, color: 'text.secondary' }}>
              Loading tasks...
            </Typography>
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <TaskIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>
              No tasks found
            </Typography>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.disabled', mt: 0.5 }}>
              {statusFilter !== 'ALL'
                ? `No ${TaskService.getStatusLabel(statusFilter).toLowerCase()} tasks`
                : 'Tasks appear here once work orders are created'}
            </Typography>
          </Box>
        ) : isMobile ? (
          // Mobile Card View
          <Box sx={{ p: 1.5 }}>
            {visibleTasks.map((task) => (
              <Card
                key={task.work_order_task_id}
                variant="outlined"
                sx={{ mb: 1.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => handleViewTask(task)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                      {task.task_number || '—'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {canSeeAllTasks && (
                        <Chip label={task.task_type === 'INTERNAL' ? 'Internal' : 'External'} size="small"
                          variant="outlined" sx={{ fontSize: fontSize.xs, height: 22 }} />
                      )}
                      <Chip
                        label={TaskService.getStatusLabel(statusOf(task))}
                        color={TaskService.getStatusColor(statusOf(task))}
                        size="small"
                        sx={{ fontSize: fontSize.xs, height: 22 }}
                      />
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500, mb: 0.5 }}>
                    {task.task_name}
                  </Typography>
                  {task.customer_name && (
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                      {task.customer_name}
                    </Typography>
                  )}
                  {task.work_order_number && (
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                      WO: {task.work_order_number}
                    </Typography>
                  )}
                  {task.assigned_to_name && (
                    <Typography sx={{ fontSize: fontSize.xs, color: 'info.main', mt: 0.5 }}>
                      Assigned: {task.assigned_to_name}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop Table View
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead sx={getBrandedTableHeadSx(theme)}>
                  <TableRow>
                    {colPrefs.visibleColumns.map((col) => (
                      <TableCell key={col.key} sx={HEAD_CELL_SX}>{col.label}</TableCell>
                    ))}
                    <TableCell align="center" sx={HEAD_CELL_SX}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleTasks.map((task) => (
                    <TableRow
                      key={task.work_order_task_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewTask(task)}
                    >
                      {colPrefs.visibleColumns.map((col) => (
                        <TableCell key={col.key} sx={{ ...CELL_SX, ...col.sx }}>
                          {col.render(task)}
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ ...CELL_SX, py: 0.25, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={task.internal_task_id ? 'View Details' : 'Open Work Order'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTask(task);
                            }}
                          >
                            {task.internal_task_id
                              ? <ViewIcon fontSize="small" />
                              : <OpenWorkOrderIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {task.internal_task_id && statusOf(task) !== 'COMPLETED' && (
                          <Tooltip title={!task.assigned_to_name ? 'Assign first' : 'Mark Complete'}>
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={!task.assigned_to_name}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickComplete(task);
                                }}
                              >
                                <CompleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalTasks}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => changePageSize(parseInt(e.target.value, 10))}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: fontSize.sm } }}
            />
          </>
        )}
      </Card>

      {/* Task Detail Dialog */}
      <TaskDetail
        open={detailDialogOpen}
        taskId={selectedTaskId}
        onClose={handleDetailClose}
      />
    </Box>
  );
};

export default Tasks;
